import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import type { Size } from '../../types'

/**
 * JPG Academy Badge
 *
 * Small labels for status, categories, and metadata.
 * Motion is built in and respects prefers-reduced-motion.
 */

type BadgeVariant = 'navy' | 'maroon' | 'gold' | 'cream' | 'success' | 'warning' | 'error' | 'info'

const variantStyles: Record<BadgeVariant, string> = {
	navy: 'bg-navy text-cream-50',
	maroon: 'bg-maroon text-cream-50',
	gold: 'bg-gold text-navy-900',
	cream: 'bg-cream text-navy-700 border border-cream-dark',
	success: 'bg-success/15 text-success',
	warning: 'bg-warning/15 text-warning',
	error: 'bg-error/15 text-error',
	info: 'bg-info/15 text-info',
}

const sizeStyles: Record<Size, string> = {
	sm: 'px-1.5 py-0.5 text-[10px]',
	md: 'px-2.5 py-0.5 text-xs',
	lg: 'px-3 py-1 text-sm',
	xl: 'px-4 py-1.5 text-sm',
}

export interface BadgeProps {
	className?: string
	children?: React.ReactNode
	variant?: BadgeVariant
	size?: Size
	dot?: boolean
}

export function Badge({ className, variant = 'navy', size = 'md', dot = false, children }: BadgeProps) {
	return (
		<motion.span
			className={cn(
				'inline-flex items-center font-ui font-semibold uppercase tracking-wider rounded-full leading-tight whitespace-nowrap',
				variantStyles[variant],
				sizeStyles[size],
				className,
			)}
			initial={{ opacity: 0, scale: 0.8 }}
			animate={{ opacity: 1, scale: 1 }}
			transition={{ type: 'spring', stiffness: 300, damping: 25 }}
		>
			{dot && <motion.span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />}
			{children}
		</motion.span>
	)
}
